import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import { useTranslations } from 'use-intl';
import { useFormatters } from '@financialharmony/ui';
import { displayOrganizationNumber } from '@financialharmony/tax-se';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency } from '@/utils/paymentUtils';
import { taxAuthorityClosedMessage, taxConnectNotAllowedMessage } from '@/utils/errorUtils';
import {
  taxConnectRefusal,
  taxGrantStage,
  type TaxConnectRefusal,
  type TaxGrantStage,
} from '@financialharmony/ui/tax/grantStage';
import {
  taxPermissionState,
  type TaxPermissionId,
  type TaxPermissionState,
} from '@financialharmony/ui/tax/permissionState';
import {
  formatTaxClosedUntil,
  formatTaxSyncedAt,
  taxRowActions,
  taxRowReason,
} from '@financialharmony/ui/tax/rowModel';
import { useTaxAccountReasons } from '@/i18n/useTaxAccountReasons';
import {
  TaxAccountApiClient,
  type TaxAccountHolder,
  type TaxAccountListItem,
  type TaxAccountView,
} from '@/clients/taxAccountApiClient';
import { useTaxNoAccountDetail } from '@/i18n/useTaxNoAccountDetail';
import TaxAccountRowActions from './TaxAccountRowActions';

/**
 * The consultant's view of a client's tax account, and the way to connect one (FIN-716).
 *
 * Deliberately NOT a reuse of `packages/ui`'s TaxConnectSheet: `src/` does not consume
 * `@financialharmony/ui` at all — it is not even a dependency — so importing it here would
 * mean taking that dependency on for one component. The shared-component migration is tracked
 * separately; until then this follows `src/`'s own conventions, the same way
 * BankConnectionDialog is a separate implementation from BankConnectSheet.
 *
 * Loading state is a plain `useState` boolean plus a spinner, per `src/`'s convention
 * (FIN-377) — `usePendingAction` is scoped to apps/client and packages/ui
 * (.claude/rules/client-loading-states.md).
 */

/** The four roles one signature grants; each is named by what it is for in the catalog. */
const PERMISSION_IDS: TaxPermissionId[] = ['tax_account', 'vat', 'employer_declarations', 'income_tax'];

const MARK_STYLE: Record<TaxPermissionState, string> = {
  granted: 'bg-green-100 text-green-700',
  missing: 'border border-dashed border-amber-500',
  unknown: 'bg-muted',
};

/**
 * The permission rows with their marks (FIN-1303, screens S4): a neutral dot until the roles
 * lookup has answered, a check per granted role, a dashed ring and "Missing" per role asked for
 * and not granted. Same states and copy as packages/ui's TaxConnectSheet.
 */
function PermissionList({ grantedRoles, showDetail }: { grantedRoles: string[] | null; showDetail: boolean }) {
  const t = useTranslations('consultantTax.section.permissions');
  return (
    <ul className="space-y-2" data-testid="tax-permission-list">
      {PERMISSION_IDS.map((id) => {
        const state: TaxPermissionState = taxPermissionState(id, grantedRoles);
        return (
          <li
            key={id}
            className="flex items-start gap-2.5"
            data-testid={`tax-permission-${id}`}
            data-state={state}
          >
            <span
              aria-hidden="true"
              data-testid={`tax-permission-mark-${state}`}
              className={'mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-md ' + MARK_STYLE[state]}
            >
              {state === 'granted' && <Check className="h-3 w-3" />}
            </span>
            <div>
              <div className="flex items-center gap-2 text-sm font-medium">
                {t(`${id}.title`)}
                {state === 'missing' && (
                  <span
                    data-testid="tax-permission-missing"
                    className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                  >
                    {t('missing')}
                  </span>
                )}
              </div>
              {showDetail && <div className="text-sm text-muted-foreground">{t(`${id}.detail`)}</div>}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

const POLL_INTERVAL_MS = 2500;
/**
 * Bounded on purpose. An unbounded poll shows "waiting" forever once the consultant has closed
 * the other tab, which reads as broken rather than resumable — the grant itself is unaffected,
 * since the authority's link stays valid for three weeks.
 */
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

/** `signed` is screens §2d (signed, not activated yet); `failed` is §2e (cancelled). */
type Step = 'intro' | 'signing' | 'signed' | 'failed' | 'insufficient_scope' | 'done';

/** The poll answers that end the wait, and the step each lands on. The rest keep watching. */
const SETTLED_STEP: Partial<Record<TaxGrantStage, Step>> = {
  active: 'done',
  insufficient_scope: 'insufficient_scope',
  declined: 'failed',
};

/**
 * Open the authority in a new tab, reporting a blocked popup rather than swallowing it.
 *
 * `noopener` is omitted from the feature string on purpose: per spec it makes window.open
 * return null, which is indistinguishable from a block — so the tab-nabbing guard is applied
 * to the returned handle instead.
 */
function openAuthoritySignIn(url: string, blockedMessage: string, onBlocked: (message: string) => void): void {
  const opened = window.open(url, '_blank');
  if (opened) {
    opened.opener = null;
    return;
  }
  onBlocked(blockedMessage);
}

/**
 * Whether the company still has something to connect.
 *
 * Offered until an account is genuinely readable, not merely until one exists: a started-but-
 * unfinished trip and a partial grant both leave a real row behind, and hiding the trigger for
 * them leaves the flow with no way to resume — while the dialog's own copy promises the
 * authority's link stays valid for three weeks. A `closed` account is the exception: the
 * authority has shut it, so there is nothing to connect (screens.md §7, FIN-994). Same rule as
 * apps/client's `needsTaxAccountConnection`.
 */
function needsConnection(accounts: TaxAccountView[]): boolean {
  if (accounts.some((account) => account.status === 'active')) return false;
  return accounts.length === 0 || accounts.some((account) => account.status !== 'closed');
}

/**
 * One tax account in the section's list. The whole row is the target rather than a small
 * "Details" affordance: it mirrors how the bank account list behaves, and a link gives keyboard
 * and middle-click behaviour that a click handler on a div does not. The row keeps its full
 * summary (FIN-595 open question 3) — this app gains a detail page, `apps/client` deliberately
 * does not.
 *
 * The row's actions sit beside the link rather than inside it: a button nested in a link is
 * invalid markup, and a click on Discard must never also open the detail page.
 */
const TaxAccountListRow: React.FC<{
  account: TaxAccountListItem;
  canConnect: boolean;
  onConnect: () => void;
  onAccountsChanged: () => void | Promise<void>;
}> = ({ account, canConnect, onConnect, onAccountsChanged }) => {
  const t = useTranslations('consultantTax.section');
  const { locale } = useFormatters();
  const reasons = useTaxAccountReasons();
  const taxNoAccountDetail = useTaxNoAccountDetail();
  const tNoAccount = useTranslations('tax.noAccount');
  const closed = account.status === 'closed';
  // Access withdrawn (scenario D, §8b): the last figure stays, greyed, with its date.
  const accessLost = account.status === 'revoked';
  const rawReason = taxRowReason(account.balanceUnavailableReason, account.grantOutcome);
  // A closed or lost account is never "waiting for a skattekonto", whatever the reason says.
  let reason = rawReason;
  if (rawReason === 'no_tax_account' && closed) reason = 'closed';
  if (rawReason === 'no_tax_account' && accessLost) reason = 'revoked';
  const noAccountLine = reason === 'no_tax_account' ? taxNoAccountDetail(account) : null;
  // The signer said they signed and a check is confirming it: no figure to read yet, and no Finish
  // or Discard, which would mint a second link or delete what the check is about to promote.
  const activating = account.activating === true;
  const actions = taxRowActions(reason, account.discardable, activating);
  return (
    <div className="flex items-center gap-3" data-testid="tax-account-item" aria-busy={activating || undefined}>
      <Link
        to={`/tax-account/${account.id}`}
        className="flex flex-1 items-center justify-between rounded-sm outline-none transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring"
        data-testid="tax-account-row"
      >
        <div>
          <div className="flex items-center gap-2 font-medium">
            {account.accountLabel}
            {closed && (
              <Badge variant="secondary" data-testid="tax-account-closed">
                {t('closed')}
              </Badge>
            )}
            {accessLost && (
              <Badge variant="secondary" data-testid="tax-account-access-lost">
                {t('accessLost')}
              </Badge>
            )}
          </div>
          <div className="text-sm text-muted-foreground">
            {account.authorityName} · {displayOrganizationNumber(account.registrationNumber)}
          </div>
        </div>
        {activating && (
          // A placeholder where the amount goes, never a figure and never 0 kr: nothing is read yet.
          <div className="flex flex-col items-end gap-1">
            <Skeleton className="h-4 w-20" data-testid="tax-account-activating-skeleton" aria-hidden="true" />
            <div className="text-xs text-muted-foreground" data-testid="tax-account-activating">
              {account.activatingClosedUntil
                ? t('activatingClosed', {
                    authority: account.authorityName,
                    until: formatTaxClosedUntil(account.activatingClosedUntil, locale),
                  })
                : t('activating', { authority: account.authorityName })}
            </div>
          </div>
        )}
        {!activating && (account.balance ? (
          <div className="text-right">
            {/* Signed, never relabelled. This sits in the same view as bank balances, so
                a positive figure would read as money the company has. */}
            <div
              className={
                // A closed account's figure is its final position, and a revoked one's its last
                // known; neither is a current one.
                closed || accessLost
                  ? 'font-semibold tabular-nums text-muted-foreground'
                  : 'font-semibold tabular-nums'
              }
              data-testid="tax-account-balance"
            >
              {formatCurrency(Number(account.balance.amount), account.balance.currency)}
            </div>
            <div className="text-xs text-muted-foreground" data-testid="tax-account-as-of">
              {t(closed || accessLost ? 'lastBalanceAt' : 'asOf', { date: account.balance.asOfDate })}
              {/* When the schedule last read a current figure, in Swedish time — what answers "is
                  this current?" Check again exists only on rows with no figure. */}
              {!closed && !accessLost && account.syncedAt && (
                <span data-testid="tax-account-synced">
                  {' · '}
                  {t('synced', { time: formatTaxSyncedAt(account.syncedAt, new Date(), locale) })}
                </span>
              )}
            </div>
            {account.balanceNotice === 'vanished' && (
              <div className="text-xs text-muted-foreground" data-testid="tax-account-vanished">
                {tNoAccount('vanished', { authority: account.authorityName })}
              </div>
            )}
          </div>
        ) : (
          // Never a 0 kr stand-in: that would tell a company they owe nothing.
          <div className="text-sm text-muted-foreground" data-testid="tax-account-unavailable">
            {reasons.row(account.authorityName)[reason ?? 'not_connected']}
            {noAccountLine && <div className="text-xs" data-testid="tax-account-no-account-detail">{noAccountLine}</div>}
          </div>
        ))}
      </Link>
      <TaxAccountRowActions
        accountId={account.id}
        accountLabel={account.accountLabel}
        finish={actions.finish}
        discard={actions.discard}
        checkAgain={actions.checkAgain}
        authorityName={account.authorityName}
        canConnect={canConnect}
        onConnect={onConnect}
        onAccountsChanged={onAccountsChanged}
      />
    </div>
  );
};

/** How often the list is re-read while a tax account is Activating (FIN-1301). */
const ACTIVATING_REFRESH_INTERVAL_MS = 5000;
/**
 * One last read this long after the deadline, so a browser clock a little ahead of the server's
 * never reads just before the server stops calling the row Activating and then waits.
 */
const ACTIVATING_FINAL_GRACE_MS = 1500;

/** The latest end of any Activating window in the list (ISO), or null when nothing is activating. */
function latestActivatingDeadline(accounts: TaxAccountView[]): string | null {
  const deadlines = accounts
    .filter((account) => account.activating && account.activatingDeadline)
    .map((account) => account.activatingDeadline as string);
  if (deadlines.length === 0) return null;
  return deadlines.reduce((latest, next) => (Date.parse(next) > Date.parse(latest) ? next : latest));
}

/**
 * Re-reads the list every few seconds while an account is Activating, until the deadline the
 * server states plus one last read, never open-ended. The deadline is the check's own while it
 * runs, then the end of the grace period for the first balance read (FIN-1339): the server keeps
 * the row loading until the figure arrives or that grace ends, so the figure appears without a
 * reload. Past the deadline the server no longer calls the row Activating, so the final read shows
 * it as unfinished (or its reading-missing reason). The read is never awaited.
 */
function useActivatingRefresh(accounts: TaxAccountView[] | null, reload: () => void | Promise<void>) {
  const deadline = accounts ? latestActivatingDeadline(accounts) : null;
  // Through a ref so a new callback identity after each reload never restarts the timers.
  const reloadRef = useRef(reload);
  useEffect(() => {
    reloadRef.current = reload;
  });

  useEffect(() => {
    if (!deadline) return;
    const finalReadAt = Date.parse(deadline) + ACTIVATING_FINAL_GRACE_MS;
    if (Number.isNaN(finalReadAt) || finalReadAt <= Date.now()) return;

    const interval = setInterval(() => void reloadRef.current(), ACTIVATING_REFRESH_INTERVAL_MS);
    const final = setTimeout(() => {
      clearInterval(interval);
      void reloadRef.current();
    }, finalReadAt - Date.now());
    return () => {
      clearInterval(interval);
      clearTimeout(final);
    };
  }, [deadline]);
}

interface TaxAccountSectionProps {
  /** Injected so the page owns the fetch; the section renders nothing when this is null. */
  accounts: TaxAccountListItem[] | null;
  /** Arrived from the detail page's Reconnect (FIN-997): open the connect dialog straight away. */
  openOnArrival?: boolean;
  onConnected: () => void | Promise<void>;
  /** Reloads the accounts after a row action changed them, e.g. a discarded setup. */
  onAccountsChanged: () => void | Promise<void>;
  companyName: string | null;
  registrationNumber: string | null;
  /** From the authority registry via the connect context (scenario Q) — never hardcoded here. */
  authorityName: string | null;
  /** From the legal form's profile via the connect context: who will see the account (FIN-1167 F1). */
  accountHolder?: TaxAccountHolder | null;
  /** The server's answer to "may this company start a connect" (FIN-1640); false when absent. */
  canConnectTaxAccount?: boolean;
  /** Why not: "closed_for_legal_form" hides the entry point, "legal_form_unknown" keeps it so the refusal explains. */
  taxConnectReason?: string;
  /** The company's client users, staff not counted (FIN-1335). */
  clientUserCount?: number;
}

const TaxAccountSection: React.FC<TaxAccountSectionProps> = ({
  accounts,
  onConnected,
  onAccountsChanged,
  companyName,
  registrationNumber,
  authorityName,
  accountHolder = null,
  canConnectTaxAccount = false,
  taxConnectReason = '',
  clientUserCount = 0,
  openOnArrival = false,
}) => {
  const t = useTranslations('consultantTax.section');
  const tNoAccount = useTranslations('tax.noAccount');
  // The latest translator, read inside the long-lived connect callbacks so a new `t` identity never rebuilds them.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  });
  // The dialog is reachable only while the connect context is in hand (see `connectable`), so
  // this empty fallback is never rendered — it only keeps the copy below typed as a string.
  const authority = authorityName ?? '';
  const connectable = Boolean(registrationNumber && authorityName);
  const [open, setOpen] = useState(false);
  const [step, setStepState] = useState<Step>('intro');
  // The step as of now, for "I've signed" resuming after its await: the poll can move the dialog
  // on while the press is being recorded, and the press must not close what the poll just showed.
  const stepRef = useRef<Step>('intro');
  const setStep = useCallback((next: Step) => {
    stepRef.current = next;
    setStepState(next);
  }, []);
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  // "I've signed" is being recorded; its failure is worded in the dialog, apart from `error`,
  // which the connect attempt owns.
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const accountIdRef = useRef<string | null>(null);
  const [grantUrl, setGrantUrl] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  // Skatteverket's scheduled closure, while the poll keeps waiting through it (FIN-1003).
  const [waitingNotice, setWaitingNotice] = useState<string | null>(null);
  // Why the first reading is missing, so the last step never claims a balance was read.
  const [readNotice, setReadNotice] = useState<string | null>(null);
  // Whether the first reading left a figure to show. A read can succeed and still have none — an
  // incomplete read is withheld — so the last step claims a read only when there is one (FIN-1012).
  const [balanceShown, setBalanceShown] = useState(false);
  // The authority shows no tax account for the company yet: connected, nothing to read (FIN-1453).
  const [noTaxAccount, setNoTaxAccount] = useState<string | null>(null);
  // The roles the lookup listed as live, for the permission marks; null until it has answered.
  const [grantedRoles, setGrantedRoles] = useState<string[] | null>(null);
  // The first reading was refused for good, and by which authority, named as the API names it:
  // closed (FIN-1035) — no balance coming, tonight or ever — or no access (FIN-1111), where the
  // grant did not take and the last step offers to sign again instead of saying connected.
  const [refusedBy, setRefusedBy] = useState<{ refusal: TaxConnectRefusal; authority: string } | null>(null);
  // True once an attempt has actually created an account server-side. Any such attempt leaves a
  // row behind — `pending` if they never finished, `insufficient_scope` if they signed for only
  // part of it — and the page must reflect that on close, or it keeps offering to connect an
  // account that already exists.
  const accountTouchedRef = useRef(false);
  // Identifies the current attempt so a poll left running by a closed dialog stops writing
  // state instead of reopening it later on a success nobody is watching for.
  const attemptRef = useRef(0);

  useEffect(() => () => {
    // Unmount abandons any in-flight poll for the same reason.
    attemptRef.current += 1;
  }, []);

  // Arriving from the detail page's Reconnect opens the dialog once the accounts are in — only
  // where there is something to reconnect — until it is closed once. Derived, not set in an effect.
  const [arrivalClosed, setArrivalClosed] = useState(false);
  const openForArrival =
    openOnArrival && !arrivalClosed && accounts !== null && needsConnection(accounts) && connectable;

  const closeDialog = useCallback((next: boolean) => {
    if (!next) {
      attemptRef.current += 1;
      setStep('intro');
      setError(null);
      setConfirmError(null);
      setIsConfirming(false);
      setGrantUrl(null);
      setTimedOut(false);
      setWaitingNotice(null);
      setReadNotice(null);
      setBalanceShown(false);
      setNoTaxAccount(null);
      setGrantedRoles(null);
      setRefusedBy(null);
      setIsConnecting(false);
      setArrivalClosed(true);
      if (accountTouchedRef.current) {
        accountTouchedRef.current = false;
        // Not only on success: a partial grant and an unfinished trip both leave a real
        // account whose own row explains where things stand. Reloading only on `done` left
        // the page offering "+ Tax account" for a company that already had one.
        void onConnected();
      }
    }
    setOpen(next);
  }, [onConnected, setStep]);

  /**
   * Watch for the outcome until the authority answers or we give up.
   *
   * Split out of handleConnect deliberately: the orchestration and the waiting are two
   * different jobs, and keeping them in one function pushed it past the complexity the lint
   * rule allows — which was a fair complaint, not a threshold to argue with.
   */
  const pollForOutcome = useCallback(
    async (accountId: string, attempt: number): Promise<Step | 'timeout' | 'abandoned'> => {
      const deadline = Date.now() + POLL_TIMEOUT_MS;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        if (attemptRef.current !== attempt) return 'abandoned';

        let polled: Awaited<ReturnType<typeof TaxAccountApiClient.checkGrant>>;
        try {
          polled = await TaxAccountApiClient.checkGrant(accountId);
        } catch (error) {
          // A failed lookup is not a failed grant — the signature may have gone through
          // perfectly and the roles endpoint simply be unreachable. Keep waiting, and say so
          // when the reason is Skatteverket's scheduled closure.
          const closed = taxAuthorityClosedMessage(error);
          if (closed && attemptRef.current === attempt) setWaitingNotice(closed);
          continue;
        }
        if (attemptRef.current !== attempt) return 'abandoned';
        setWaitingNotice(null);
        // Same rule as the client app (packages/ui/tax/grantStage.ts): the status decides, and
        // the latest trip's recorded outcome only refines a pending account.
        const stage = taxGrantStage(polled.account.status, polled.grantOutcome);
        const settled = SETTLED_STEP[stage];
        if (settled) {
          // The partial-grant step marks from this answer; a live grant takes the freshest one from
          // its first reading below.
          setGrantedRoles(polled.account.grantedRoles ?? null);
          return settled;
        }
        // Signed but not live (§2d): say so, and keep watching in case it goes live meanwhile.
        if (stage === 'signed') setStep('signed');
      }
      return 'timeout';
    },
    [setStep],
  );

  /**
   * "I've signed": have the server confirm the signature in the background, then close. Awaits
   * the one fast write and closes only when it was recorded, so a failed press stays open with the
   * reason instead of looking like activation. Closing refreshes the list without waiting on it.
   */
  const handleConfirmSigned = useCallback(async () => {
    const accountId = accountIdRef.current;
    if (!accountId) return;
    const attempt = attemptRef.current;
    setConfirmError(null);
    setIsConfirming(true);
    try {
      await TaxAccountApiClient.confirmSigned(accountId);
    } catch {
      if (attemptRef.current === attempt) {
        setConfirmError(tRef.current('dialog.confirmFailed'));
        setIsConfirming(false);
      }
      return;
    }
    // Closed meanwhile: the press stands, and there is nothing left to close.
    if (attemptRef.current !== attempt) return;
    setIsConfirming(false);
    // A grant that went live while the press was in flight has its own step to show.
    if (stepRef.current !== 'signing') return;
    closeDialog(false);
  }, [closeDialog]);

  const handleConnect = useCallback(async () => {
    setError(null);
    setConfirmError(null);
    setIsConnecting(true);
    const attempt = ++attemptRef.current;

    let started: { account: TaxAccountView; grantUrl: string };
    try {
      started = await TaxAccountApiClient.startGrant();
    } catch (error) {
      // The client already surfaced any other provider failure through handleError; this is the
      // in-dialog copy, deliberately generic — except Skatteverket's scheduled closure, whose
      // own sentence says when to come back (FIN-1003), and a connect refused for the company's
      // legal form, which no retry gets past (FIN-1130, FIN-1167).
      if (attemptRef.current === attempt) {
        setError(
          taxAuthorityClosedMessage(error) ??
            taxConnectNotAllowedMessage(error) ??
            tRef.current('dialog.startFailed'),
        );
        setIsConnecting(false);
      }
      return;
    }
    if (attemptRef.current !== attempt) return;

    accountTouchedRef.current = true;
    accountIdRef.current = started.account.id;
    setGrantUrl(started.grantUrl);
    setStep('signing');
    openAuthoritySignIn(started.grantUrl, tRef.current('dialog.popupBlocked', { authority }), (message) => setError(message));

    const outcome = await pollForOutcome(started.account.id, attempt);
    if (outcome === 'abandoned' || attemptRef.current !== attempt) return;

    if (outcome === 'timeout') {
      setTimedOut(true);
      setIsConnecting(false);
      return;
    }
    if (outcome === 'insufficient_scope' || outcome === 'failed') {
      setStep(outcome);
      setIsConnecting(false);
      return;
    }

    // Take the reading here: a grant with no figure behind it would leave the last step
    // claiming a balance that was never read.
    let shown = false;
    let noAccount: string | null = null;
    let refused: { refusal: TaxConnectRefusal; authority: string } | null = null;
    try {
      const read = await TaxAccountApiClient.refresh(started.account.id);
      shown = Boolean(read.balance);
      if (!read.balance && read.balanceUnavailableReason === 'no_tax_account') noAccount = read.authorityName;
      setGrantedRoles(read.grantedRoles ?? null);
      // The read can also be refused for good: closed at the authority (felkod 5), or no access.
      const refusal = taxConnectRefusal(read.balanceUnavailableReason);
      if (refusal) refused = { refusal, authority: read.authorityName };
    } catch (error) {
      // A failed first read is not a failed connection — the row says why itself, and the last
      // step says the reading is missing instead of claiming it was taken.
      if (attemptRef.current === attempt) setReadNotice(taxAuthorityClosedMessage(error) ?? tRef.current('dialog.firstReadFailed', { authority }));
    }
    if (attemptRef.current !== attempt) return;
    setBalanceShown(shown);
    setNoTaxAccount(noAccount);
    setRefusedBy(refused);
    setStep('done');
    setIsConnecting(false);
    accountTouchedRef.current = false;
    await onConnected();
  }, [authority, onConnected, pollForOutcome, setStep]);

  useActivatingRefresh(accounts, onAccountsChanged);

  // The feature is off, or unreachable. Render nothing at all rather than "no tax account
  // yet", which would be a claim about this company.
  if (accounts === null) return null;

  const canConnect = needsConnection(accounts) && connectable;
  // A UI-state rule, not a capability: an account that already exists keeps its own Finish and
  // Reconnect, so only the entry point for a first connect closes. The server refuses either way.
  const entryOffered = accounts.length > 0 || canConnectTaxAccount || taxConnectReason === 'legal_form_unknown';

  /** The last step's title: connected, unless the first reading was refused for good. */
  const doneTitle = (): string => {
    if (refusedBy?.refusal === 'closed') return t('dialog.titleClosed', { authority: refusedBy.authority });
    if (refusedBy?.refusal === 'revoked') return t('dialog.titleRevoked');
    return t('dialog.titleDone');
  };

  return (
    <div className="space-y-4" data-testid="tax-account-section">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">{t('title')}</h2>
        {canConnect && entryOffered && (
          <Button
            variant="outline"
            data-testid="tax-account-connect"
            onClick={() => setOpen(true)}
          >
            {accounts.some((account) => account.status === 'revoked') ? t('reconnect') : t('connect')}
          </Button>
        )}
      </div>

      {accounts.length > 0 && (
        <Card>
          <CardContent className="space-y-4 pt-6">
            {accounts.map((account) => (
              <TaxAccountListRow
                key={account.id}
                account={account}
                canConnect={canConnect}
                onConnect={() => setOpen(true)}
                onAccountsChanged={onAccountsChanged}
              />
            ))}
          </CardContent>
        </Card>
      )}

      <Dialog open={open || openForArrival} onOpenChange={closeDialog}>
        <DialogContent data-testid="tax-connect-dialog">
          <DialogHeader>
            <DialogTitle>
              {step === 'intro' && t('dialog.titleIntro', { authority })}
              {step === 'signing' && (timedOut ? t('dialog.titleStillWaiting') : t('dialog.titleSigning', { authority }))}
              {step === 'signed' && t('dialog.titleSigned')}
              {step === 'failed' && t('dialog.titleFailed')}
              {step === 'insufficient_scope' && t('dialog.titleAlmost')}
              {step === 'done' && doneTitle()}
            </DialogTitle>
            {step === 'intro' && (
              <DialogDescription>{t('dialog.consent', { authority })}</DialogDescription>
            )}
          </DialogHeader>

          {step === 'intro' && (
            <div className="space-y-3">
              <PermissionList grantedRoles={null} showDetail />

              <div className="rounded-md bg-muted px-3 py-2" data-testid="tax-connect-company">
                <div className="text-sm font-medium">
                  {companyName} · {displayOrganizationNumber(registrationNumber)}
                </div>
                <div className="text-sm text-muted-foreground">{t('dialog.company')}</div>
                {accountHolder === 'owner' && (
                  <div className="mt-2 border-t pt-2 text-sm" data-testid="tax-connect-personal-notice">
                    {t('dialog.personalNotice')}
                    {clientUserCount > 1 && (
                      <div className="mt-1 text-muted-foreground" data-testid="tax-connect-people-count">
                        {t('dialog.peopleCount', { count: clientUserCount })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Contractually required by §8 of the API's Allmänna villkor — asserted in tests
                  rather than left to survive future copy edits. */}
              <p className="text-xs text-muted-foreground">
                {t('dialog.legal', { count: String(PERMISSION_IDS.length), authority })}
              </p>
            </div>
          )}

          {step === 'signing' && (
            <div className="space-y-3" data-testid="tax-connect-signing">
              {timedOut ? (
                <p className="text-sm text-muted-foreground" data-testid="tax-connect-timed-out">
                  {t('dialog.timedOut')}
                </p>
              ) : (
                <div
                  className="flex items-center gap-3 text-sm text-muted-foreground"
                  data-testid="tax-connect-waiting"
                >
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  {t('dialog.waiting', { authority })}
                </div>
              )}
              {waitingNotice && !timedOut && (
                <Alert data-testid="tax-connect-waiting-notice">
                  <AlertDescription>{waitingNotice}</AlertDescription>
                </Alert>
              )}
              {confirmError && (
                <Alert variant="destructive" data-testid="tax-connect-confirm-error">
                  <AlertDescription>{confirmError}</AlertDescription>
                </Alert>
              )}
              {grantUrl && (
                <a
                  href={grantUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium underline"
                  data-testid="tax-connect-reopen"
                >
                  {t('dialog.reopen', { authority })}
                </a>
              )}
            </div>
          )}

          {step === 'signed' && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground" data-testid="tax-connect-signed">
                {t('dialog.signedBody', { authority })}
              </p>
              {/* Signed is not granted: nothing is checked until the authority lists the role as live. */}
              <PermissionList grantedRoles={null} showDetail={false} />
            </div>
          )}

          {step === 'failed' && (
            <p className="text-sm text-muted-foreground" data-testid="tax-connect-failed">
              {t('dialog.failedBody')}
            </p>
          )}

          {step === 'insufficient_scope' && (
            <div className="space-y-3" data-testid="tax-connect-insufficient-scope">
              <p className="text-sm text-muted-foreground">{t('dialog.insufficientBody')}</p>
              <PermissionList grantedRoles={grantedRoles} showDetail={false} />
              <p className="text-sm text-muted-foreground">{t('dialog.insufficientHint', { authority })}</p>
              {/*
                Starts a fresh grant rather than relinking to the one already signed (FIN-751).
                Whether an already-signed djuplänk can be signed again is not established — the
                spec's "valid three weeks" covers an *unfinished* trip — and `startGrant` moves
                the account back to `pending`, which is what makes the wait honest: reusing the
                link would leave it reading `insufficient_scope` while the user signs, and the
                poll would take that stale value as the answer and bounce straight back here.
                `granted_roles` is not cleared, so nothing already signed for is lost.
              */}
              <Button
                onClick={() => void handleConnect()}
                disabled={isConnecting}
                data-testid="tax-connect-add-role"
              >
                {isConnecting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('dialog.addMissing')}
              </Button>
            </div>
          )}

          {step === 'done' && !refusedBy && <PermissionList grantedRoles={grantedRoles} showDetail={false} />}
          {step === 'done' && refusedBy?.refusal === 'closed' && (
            <p className="text-sm text-muted-foreground" data-testid="tax-connect-closed">
              {t('dialog.closedBody', { authority: refusedBy.authority })}
            </p>
          )}
          {step === 'done' && refusedBy?.refusal === 'revoked' && (
            <p className="text-sm text-muted-foreground" data-testid="tax-connect-revoked">
              {t('dialog.revokedBody', { authority: refusedBy.authority })}
            </p>
          )}
          {step === 'done' && !refusedBy && readNotice && (
            <Alert data-testid="tax-connect-read-notice">
              <AlertDescription>
                {readNotice} {t('dialog.readNoticeSuffix')}
              </AlertDescription>
            </Alert>
          )}
          {step === 'done' && !refusedBy && !readNotice && balanceShown && (
            <p className="text-sm text-muted-foreground" data-testid="tax-connect-done-copy">
              {t('dialog.balanceRead')}
            </p>
          )}
          {step === 'done' && !refusedBy && !readNotice && noTaxAccount !== null && (
            <p className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground" data-testid="tax-connect-no-account">
              {tNoAccount('explanation', { authority: noTaxAccount ?? authority })}
            </p>
          )}
          {step === 'done' && !refusedBy && !readNotice && !balanceShown && noTaxAccount === null && (
            <p className="text-sm text-muted-foreground" data-testid="tax-connect-no-balance">
              {t('dialog.noBalance')}
            </p>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            {step === 'intro' && (
              <>
                <Button variant="ghost" onClick={() => closeDialog(false)} disabled={isConnecting}>
                  {t('dialog.cancel')}
                </Button>
                <Button
                  onClick={handleConnect}
                  disabled={isConnecting}
                  data-testid="tax-connect-submit"
                >
                  {isConnecting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('dialog.continueTo', { authority })}
                </Button>
              </>
            )}
            {step === 'signing' && (
              <>
                {/* Later starts nothing and asks the server nothing: the row is simply unfinished. */}
                <Button
                  variant="ghost"
                  onClick={() => closeDialog(false)}
                  disabled={isConfirming}
                  data-testid="tax-connect-later"
                >
                  {t('dialog.later')}
                </Button>
                <Button
                  onClick={() => void handleConfirmSigned()}
                  disabled={isConfirming}
                  data-testid="tax-connect-signed-confirm"
                >
                  {isConfirming && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('dialog.signed')}
                </Button>
              </>
            )}
            {step === 'signed' && (
              <Button onClick={() => closeDialog(false)} data-testid="tax-connect-signed-done">
                {t('dialog.done')}
              </Button>
            )}
            {/* A declined trip (§2e) and a grant whose first read was refused (FIN-1111) end the same
                way: close, or start a fresh grant with a new link — never the spent one (FIN-751). */}
            {(step === 'failed' || (step === 'done' && refusedBy?.refusal === 'revoked')) && (
              <>
                <Button
                  variant="ghost"
                  onClick={() => closeDialog(false)}
                  disabled={isConnecting}
                  data-testid="tax-connect-close"
                >
                  {t('dialog.close')}
                </Button>
                <Button
                  onClick={() => void handleConnect()}
                  disabled={isConnecting}
                  data-testid={step === 'failed' ? 'tax-connect-retry' : 'tax-connect-reconnect'}
                >
                  {isConnecting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {step === 'failed' ? t('dialog.tryAgain') : t('dialog.signAgain')}
                </Button>
              </>
            )}
            {step === 'insufficient_scope' && (
              <Button variant="ghost" onClick={() => closeDialog(false)} data-testid="tax-connect-close">
                {t('dialog.close')}
              </Button>
            )}
            {step === 'done' && refusedBy?.refusal !== 'revoked' && (
              <Button onClick={() => closeDialog(false)} data-testid="tax-connect-done">
                {t('dialog.done')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaxAccountSection;
